import { Popover, Tag, Typography } from 'antd';
import { citeChips } from '../packView';
import type { Claim, FactInfo, NoteSection } from '../types';

const LIST = { margin: '4px 0 8px', paddingLeft: 20 };

/** A cited fact; hover shows label, value and source (spec §10), plus the tool and fact ID. */
const FactChip = ({ id, fact }: { id: string; fact: FactInfo }) => (
  <Popover
    trigger={['hover', 'focus']}
    title={fact.label}
    content={
      <div style={{ maxWidth: 320 }}>
        <div>
          <Typography.Text strong>Value: </Typography.Text>
          {fact.value === null ? '—' : String(fact.value)}
        </div>
        <div>
          <Typography.Text strong>Source: </Typography.Text>
          {fact.source || 'unknown'}
        </div>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 11, wordBreak: 'break-all' }}
        >
          {`${fact.tool} · ${id}`}
        </Typography.Text>
      </div>
    }
  >
    <Tag
      color="blue"
      tabIndex={0}
      style={{ cursor: 'default', marginInlineEnd: 4 }}
    >
      {fact.label}
    </Tag>
  </Popover>
);

type ClaimListProps = {
  title?: string;
  claims: Claim[];
  facts: Record<string, FactInfo>;
};

/**
 * One bullet per claim. Claim text is model output, shown as plain text. A cite
 * the pack has no fact for is shown as its ID in plain text (Plan 4a).
 */
export const ClaimList = ({ title, claims, facts }: ClaimListProps) =>
  claims.length === 0 ? null : (
    <div>
      {title && <Typography.Text type="secondary">{title}</Typography.Text>}
      <ul style={LIST}>
        {claims.map((claim, i) => (
          <li key={i}>
            {claim.text}{' '}
            {citeChips(claim, facts).map((chip, j) =>
              chip.fact ? (
                <FactChip
                  key={`${chip.id}-${j}`}
                  id={chip.id}
                  fact={chip.fact}
                />
              ) : (
                <Typography.Text key={`${chip.id}-${j}`} type="secondary" code>
                  {chip.id}
                </Typography.Text>
              ),
            )}
          </li>
        ))}
      </ul>
    </div>
  );

/** A note's non-empty sections, in contract order. */
export const NoteSections = ({
  sections,
  facts,
}: {
  sections: NoteSection[];
  facts: Record<string, FactInfo>;
}) => (
  <>
    {sections
      .filter((section) => section.claims.length > 0)
      .map((section) => (
        <ClaimList
          key={section.key}
          title={section.label}
          claims={section.claims}
          facts={facts}
        />
      ))}
  </>
);
